import { useState } from "react";
import { config, renderTokens } from "@/config/eventConfig";

const Section = ({ label, title, children }) => (
  <section className="mb-14">
    <p className="mono-label text-ink/50 mb-2">{label}</p>
    <h2 className="font-display text-3xl leading-none mb-6">{title}</h2>
    {children}
  </section>
);

export const MentorResources = () => {
  const m = config.mentors;
  const [openNote, setOpenNote] = useState(null);

  return (
    <div data-testid="mentor-resources">
      <Section label="01" title="The one rule">
        <div className="bg-white border border-sand border-l-[3px] border-l-teal p-6 text-lg">
          {m.one_rule}
        </div>
      </Section>

      <Section label="02" title={m.room.title}>
        <ul className="space-y-3 mb-5">
          {(m.room.snapshot && m.room.snapshot.length ? m.room.snapshot : m.room.points).map((p, i) => (
            <li key={i} className="bg-white border border-sand p-4 text-ink/80">{p}</li>
          ))}
        </ul>
        <p className="callout-guidance text-teal">{m.room.closer}</p>
      </Section>

      <Section label="03" title="Your role">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {m.roles.map((r) => (
            <div key={r.name} className="bg-white border border-sand p-5">
              <p className="font-semibold mb-2">{r.name}</p>
              <p className="text-ink/70 text-sm">{renderTokens(r.body)}</p>
            </div>
          ))}
        </div>
        <div className="border border-sand">
          {m.allocation.map((a, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 px-5 py-4 border-b border-sand last:border-b-0">
              <span className="mono-label text-teal">{a.ratio}</span>
              <span className="text-ink/80 text-sm">{a.guidance}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section label="04" title="The data rule">
        <div className="callout-warning text-ink/80">
          {config.data_rule}
          <p data-testid="data-rule-note" className="mt-3">{m.data_rule_note}</p>
        </div>
      </Section>

      <Section label="05" title={m.build_help.title}>
        <ul className="space-y-3">
          {m.build_help.points.map((p, i) => (
            <li key={i} className="bg-white border border-sand border-l-[3px] border-l-teal p-4 text-ink/80">
              {p}
            </li>
          ))}
        </ul>
      </Section>

      <Section label="06" title="Your move in each segment">
        <div className="space-y-3">
          {m.segment_moves.map((s, i) => (
            <div key={i} className="bg-white border border-sand p-4">
              <p className="mono-label text-ink/50 mb-1">{s.segment}</p>
              <p className="text-ink/80">{s.move}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section label="07" title="Escalation">
        <div className="flex flex-col gap-2 mb-5">
          {m.escalation.steps.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="mono-label text-ink/40 w-6">{i + 1}</span>
              <span className="bg-white border border-sand px-4 py-3 flex-1">{s}</span>
              {i < m.escalation.steps.length - 1 && <span className="text-orange">↓</span>}
            </div>
          ))}
        </div>
        <p className="callout-guidance text-teal">{m.escalation.closer}</p>
      </Section>

      <Section label="08" title="Per-recipe coaching notes">
        <div className="border border-sand">
          {config.recipes.map((r) => {
            const note = m.recipe_notes[r.id];
            if (!note) return null;
            const open = openNote === r.id;
            const goodFor = config.recipeDocs?.[r.id]?.description;
            return (
              <div key={r.id} className="border-b border-sand last:border-b-0">
                <button
                  data-testid={`note-toggle-${r.id}`}
                  onClick={() => setOpenNote(open ? null : r.id)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between hover:text-orange-dark transition-colors duration-150"
                >
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-orange">{open ? "–" : "+"}</span>
                </button>
                {open && (
                  <div className="px-5 pb-5 text-sm space-y-2">
                    {goodFor && (
                      <p className="text-ink/70"><span className="mono-label text-teal">Good for</span>, {renderTokens(goodFor)}</p>
                    )}
                    {note.body ? (
                      <p className="text-ink/70">{renderTokens(note.body)}</p>
                    ) : (
                      <>
                        <p className="text-ink/70"><span className="mono-label text-orange-dark">Trap</span>, {note.trap}</p>
                        <p className="text-ink/70"><span className="mono-label text-teal">Ask</span>, {note.question}</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <Section label="09" title="The fifteen-second test">
        <div className="bg-white border border-sand p-6 text-ink/80">{m.fifteen_second_test}</div>
      </Section>
    </div>
  );
};
